import { useState, useEffect, useRef, useCallback } from 'react';
import { cotizacionesAPI } from '../services/api';

/**
 * Hook para manejar el lock pesimista de una cotizacion.
 *
 * Modo 'editor':
 *   - Toma el lock al mount. Si esta tomado por otro user: state.bloqueadoPor.
 *   - Heartbeat cada 20s renueva el TTL del lock.
 *   - Al desmontar (cambio de ruta, cerrar form) libera el lock.
 *   - Al cerrar el tab/ventana, intenta liberar via sendBeacon.
 *   - Detecta solicitudes pendientes (cuando otro user solicita) → state.solicitud.
 *
 * Modo 'viewer':
 *   - Polling cada 20s para mostrar quien edita (si alguien edita).
 *   - No toma ningun lock.
 *
 * Optimizaciones anti-saturacion:
 *   - Pausa polling/heartbeat cuando la tab NO esta visible (visibilitychange).
 *   - Backoff de 60s ante 429/5xx: skip los siguientes ticks.
 *   - Al volver a la tab, refresca inmediato para no dejar UI desactualizada.
 *   - **Lock lost detection**: si el heartbeat devuelve 409 despues de haber
 *     tenido el lock, corta TODOS los timers de inmediato (evita heartbeats
 *     infinitos consumiendo el rate limit) y dispara onLockLost({ nuevoOwner }).
 *
 * Opciones:
 *   modo         → 'editor' | 'viewer' (default 'viewer')
 *   enabled      → activa/desactiva el hook (default true)
 *   onLockLost   → callback(nuevoOwner) al detectar que otro user tomo control
 *
 * API publica:
 *   lock          → estado actual del lock o null
 *   bloqueadoPor  → { usuario_nombre } si modo editor y otro lo tiene (no pudo tomar)
 *   solicitud     → solicitud pendiente de otro user (modo editor)
 *   loading       → true mientras hace primer fetch
 *   liberar()     → libera el lock manualmente (para "Ceder" o "Guardar")
 *   solicitar()   → manda solicitud al owner (modo viewer)
 *   rechazarSolicitud() → editor rechaza solicitud pendiente
 *   refrescar()   → re-fetch del estado actual
 *   forzar()      → toma control forzado (requiere permiso backend)
 */
// Heartbeat del editor: cada 20s renueva el lock y trae solicitudes pendientes.
// Costo: ~3 requests/min por editor activo. Se pausa cuando la tab pierde foco.
const HEARTBEAT_MS = 20_000;
// Polling del viewer: cada 20s. Se pausa cuando la tab esta oculta (patron
// estandar de apps colaborativas: no actualizar lo que nadie mira).
const POLL_VIEWER_MS = 20_000;
// Backoff cuando el server responde error (429/5xx): esperar este tiempo antes
// del proximo tick para no martillar durante un incidente. Se resetea al primer OK.
const BACKOFF_MS = 60_000;

export function useCotizacionLock(cotizacionId, { modo = 'viewer', enabled = true, onLockLost } = {}) {
    const [lock, setLock] = useState(null);
    const [bloqueadoPor, setBloqueadoPor] = useState(null);
    const [solicitud, setSolicitud] = useState(null);
    const [loading, setLoading] = useState(true);

    const heartbeatTimerRef = useRef(null);
    const pollTimerRef = useRef(null);
    const cedidoRef = useRef(false); // si el user cedio, no renovar mas
    const liberadoRef = useRef(false);
    // Backoff activo tras un error: skip el proximo tick para no martillar.
    const skipUntilRef = useRef(0);
    // Ref al callback para no re-setupear el heartbeat si cambia la funcion.
    const onLockLostRef = useRef(onLockLost);
    useEffect(() => { onLockLostRef.current = onLockLost; }, [onLockLost]);
    // Guard para disparar handleLockLost UNA sola vez.
    const lostFiredRef = useRef(false);
    // Guard para saber si ya tuvimos el lock alguna vez (editor). Si SI y llega
    // 409, es lockLost real. Si NO (primer intento), es solo "no pude tomar".
    const teniaLockRef = useRef(false);
    // Ref a stopTimers para poder cancelarlos desde tomarOrenovar (fuera del useEffect).
    const stopTimersRef = useRef(() => {});

    // Helper para cargar el estado actual (sin pedir/renovar lock)
    const refrescar = useCallback(async () => {
        if (!cotizacionId) return;
        // Backoff activo: skip este tick
        if (Date.now() < skipUntilRef.current) return;
        try {
            const res = await cotizacionesAPI.getLock(cotizacionId);
            const l = res.data?.lock || null;
            setLock(l);
            setSolicitud(l?.solicitud || null);
            skipUntilRef.current = 0; // OK → reset backoff
        } catch (err) {
            // 429/5xx/red: activar backoff para no martillar durante incidentes.
            const status = err?.response?.status;
            if (status === 429 || (status >= 500 && status < 600) || !status) {
                skipUntilRef.current = Date.now() + BACKOFF_MS;
            }
        }
    }, [cotizacionId]);

    // Handler de lockLost: se dispara UNA vez cuando el editor pierde el lock
    // (otro user lo tomo a la fuerza). Cancela timers, marca cedido y notifica.
    // Sin esto, el heartbeat seguiria disparando 409 cada 20s eternamente.
    const handleLockLost = useCallback((nuevoOwner) => {
        if (lostFiredRef.current) return;
        lostFiredRef.current = true;
        cedidoRef.current = true;      // evita que un tick en vuelo reintente
        liberadoRef.current = true;    // el cleanup del useEffect no hara DELETE
        stopTimersRef.current();       // corta heartbeat + poll de inmediato
        try {
            onLockLostRef.current?.(nuevoOwner);
        } catch (err) {
            console.error('onLockLost callback error:', err);
        }
    }, []);

    // Toma o renueva (heartbeat) — solo en modo editor
    const tomarOrenovar = useCallback(async () => {
        if (!cotizacionId || cedidoRef.current || liberadoRef.current) return null;
        // Backoff activo: skip este tick de heartbeat
        if (Date.now() < skipUntilRef.current) return { ok: false, backoff: true };
        try {
            const res = await cotizacionesAPI.tomarLock(cotizacionId);
            if (res.status === 409 && res.data?.lock) {
                // Lock ocupado por otro user.
                // Si YA teniamos el lock antes, esto es un lockLost (nos lo forzaron).
                // Si es el primer intento, es solo "no pude tomar" — normal.
                setBloqueadoPor(res.data.lock);
                setLock(res.data.lock);
                if (teniaLockRef.current) {
                    handleLockLost(res.data.lock);
                }
                return { ok: false, bloqueadoPor: res.data.lock };
            }
            if (res.success && res.data?.lock) {
                setLock(res.data.lock);
                setSolicitud(res.data.lock.solicitud || null);
                setBloqueadoPor(null);
                teniaLockRef.current = true;
                skipUntilRef.current = 0; // OK → reset backoff
                return { ok: true, lock: res.data.lock };
            }
            return { ok: false };
        } catch (err) {
            const status = err?.response?.status || err?.status;
            // 403 = usuario sin acceso a esta cotizacion. Cortar el heartbeat
            // para siempre y no reintentar (evita cascada de 403 en los logs).
            if (status === 403) {
                cedidoRef.current = true;
                liberadoRef.current = true;
                stopTimersRef.current();
                return { ok: false, forbidden: true, error: err };
            }
            if (status === 429 || (status >= 500 && status < 600) || !status) {
                skipUntilRef.current = Date.now() + BACKOFF_MS;
            }
            return { ok: false, error: err };
        }
    }, [cotizacionId, handleLockLost]);

    // Forzar toma del lock (requiere permiso 'forzar_lock_cotizacion' en backend).
    // Reemplaza el lock actual — el editor original recibira 409 en su proximo
    // heartbeat y su hook disparara onLockLost.
    const forzar = useCallback(async () => {
        if (!cotizacionId) return { ok: false };
        try {
            const res = await cotizacionesAPI.forzarLock(cotizacionId);
            if (res.success && res.data?.lock) {
                lostFiredRef.current = false;   // reseteo por si veniamos de un lockLost
                cedidoRef.current = false;
                liberadoRef.current = false;
                teniaLockRef.current = true;
                setLock(res.data.lock);
                setBloqueadoPor(null);
                setSolicitud(null);
                return { ok: true, lock: res.data.lock, desplazado: res.data.desplazado };
            }
            return { ok: false };
        } catch (err) {
            return { ok: false, error: err };
        }
    }, [cotizacionId]);

    const liberar = useCallback(async () => {
        if (!cotizacionId) return;
        liberadoRef.current = true;
        try {
            await cotizacionesAPI.liberarLock(cotizacionId);
        } catch {
            // ignorar — el TTL se encarga si falla
        }
        if (heartbeatTimerRef.current) {
            clearInterval(heartbeatTimerRef.current);
            heartbeatTimerRef.current = null;
        }
        setLock(null);
        setSolicitud(null);
    }, [cotizacionId]);

    // Setup principal: depende del modo
    useEffect(() => {
        if (!enabled || !cotizacionId) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        liberadoRef.current = false;
        cedidoRef.current = false;
        lostFiredRef.current = false;
        teniaLockRef.current = false;

        (async () => {
            setLoading(true);
            if (modo === 'editor') {
                await tomarOrenovar();
            } else {
                await refrescar();
            }
            if (!cancelled) setLoading(false);
        })();

        // Arranca/detiene los timers segun visibilidad. Cuando la tab pierde
        // foco, no tiene sentido pollear: nadie esta mirando. Al volver, hace
        // un tick inmediato para actualizar el estado y reanuda el interval.
        const startTimers = () => {
            if (modo === 'editor') {
                if (heartbeatTimerRef.current) return;
                heartbeatTimerRef.current = setInterval(() => {
                    if (!cedidoRef.current && !liberadoRef.current) tomarOrenovar();
                }, HEARTBEAT_MS);
            } else {
                if (pollTimerRef.current) return;
                pollTimerRef.current = setInterval(refrescar, POLL_VIEWER_MS);
            }
        };
        const stopTimers = () => {
            if (heartbeatTimerRef.current) {
                clearInterval(heartbeatTimerRef.current);
                heartbeatTimerRef.current = null;
            }
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
        // Exponemos stopTimers via ref para poder cancelar desde handleLockLost
        // (que vive fuera de este closure).
        stopTimersRef.current = stopTimers;

        if (!document.hidden) startTimers();

        const onVisibility = () => {
            if (document.hidden) {
                stopTimers();
            } else {
                // Al volver a la tab, refrescar inmediato para sincronizar
                if (modo === 'editor' && !cedidoRef.current && !liberadoRef.current) {
                    tomarOrenovar();
                } else if (modo === 'viewer') {
                    refrescar();
                }
                startTimers();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        // Cleanup: cancelar timers + liberar lock si era editor
        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', onVisibility);
            stopTimers();
            if (modo === 'editor' && !liberadoRef.current) {
                // Liberacion best-effort (fire-and-forget)
                cotizacionesAPI.liberarLock(cotizacionId).catch(() => {});
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cotizacionId, modo, enabled]);

    // beforeunload: intentar liberar via sendBeacon antes de cerrar el tab.
    // sendBeacon funciona aunque la pagina se este cerrando.
    useEffect(() => {
        if (modo !== 'editor' || !cotizacionId) return;
        const onBeforeUnload = () => {
            if (liberadoRef.current) return;
            try {
                const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/cotizaciones/${cotizacionId}/lock`;
                const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
                // sendBeacon no permite headers custom. Workaround: usar fetch con keepalive.
                fetch(url, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` },
                    keepalive: true
                }).catch(() => {});
            } catch {}
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        window.addEventListener('pagehide', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            window.removeEventListener('pagehide', onBeforeUnload);
        };
    }, [cotizacionId, modo]);

    // Solicitar lock (modo viewer)
    const solicitar = useCallback(async () => {
        if (!cotizacionId) return;
        try {
            await cotizacionesAPI.solicitarLock(cotizacionId);
            await refrescar();
            return { ok: true };
        } catch (err) {
            return { ok: false, error: err };
        }
    }, [cotizacionId, refrescar]);

    // Rechazar solicitud pendiente (modo editor: click "Seguir editando")
    const rechazarSolicitud = useCallback(async () => {
        if (!cotizacionId) return;
        try {
            await cotizacionesAPI.rechazarSolicitud(cotizacionId);
            setSolicitud(null);
            return { ok: true };
        } catch (err) {
            return { ok: false, error: err };
        }
    }, [cotizacionId]);

    return {
        lock,
        bloqueadoPor,        // solo en modo editor: si no pudimos tomar el lock
        solicitud,           // solo en modo editor: si otro user solicito el lock
        loading,
        liberar,
        solicitar,
        rechazarSolicitud,   // modo editor: rechazar solicitud pendiente
        refrescar,
        forzar               // toma control forzado (requiere permiso backend)
    };
}
